import { cn } from '@/lib/utils';

import { BlockComponentModel } from '@/cms/types/blok-component';

import { NextImageProps } from '@/components/atoms/Image';
import ImageStrapi, {
  ImageStrapiProps,
} from '@/components/atoms/Image/ImageStrapi';
import { LinkButtonProps } from '@/components/atoms/LinkButton';
import { LinkButtonStrapiProps } from '@/components/atoms/LinkButton/LinkStrapi';
import Popover, { PopoverProps } from '@/components/atoms/Popover';
import { pullDataTrackers } from '@/components/molecules/imageLink/utils';

import { ImageLinkStrapi } from '../imageLink/ImageLinkStrapi';

type BasePopupImageLinkStrapiProps = {
  image: ImageStrapiProps;
};

export type PopupImageLinkStrapiBlock = PopupStrapiBlock | LinkStrapiBlock;

type LinkStrapiBlock = BasePopupImageLinkStrapiProps & {
  link: LinkButtonStrapiProps;
  popup?: ImageStrapiProps;
};

type PopupStrapiBlock = BasePopupImageLinkStrapiProps & {
  link?: LinkButtonStrapiProps;
  popup: ImageStrapiProps;
};

export type PopupImageLinkInner = {
  popup: Partial<PopoverProps>;
  popupImage: Partial<NextImageProps>;
  image: Partial<NextImageProps>;
  link: Partial<LinkButtonProps>;
};

function isLinkBlock(props: any): props is LinkStrapiBlock {
  return !!props.link;
}

export type PopupImageLinkStrapiProps = BlockComponentModel<
  PopupImageLinkStrapiBlock,
  PopupImageLinkInner
>;

export function PopupImageLinkStrapi({
  block,
  inner,
}: PopupImageLinkStrapiProps) {
  const { image, popup } = block;

  if (popup) {
    return (
      <Popover
        {...inner?.popup}
        classNames={{
          ...inner?.popup?.classNames,
          // agreed to invert the popover colors compared to the surrounding context + removed the border
          childrenWrapper: cn(
            'border shadow',
            inner?.popup?.classNames?.childrenWrapper
          ),
          arrow: cn('fill-base-black', inner?.popup?.classNames?.arrow),
        }}
        target={<ImageStrapi block={image} inner={inner?.image} />}
        showArrow={true}
        position='right'
        targetProps={{
          ...pullDataTrackers(inner?.link || {}),
        }}
      >
        <ImageStrapi block={popup} inner={inner?.popupImage} />
      </Popover>
    );
  }

  if (isLinkBlock(block)) {
    return (
      <ImageLinkStrapi
        inner={{
          ...inner?.link,
          classNames: {
            ...inner?.link?.classNames,
            image: cn('flex content-center h-6 w-6'),
          },
        }}
        block={block}
      />
    );
  }

  return <ImageStrapi block={image} inner={inner?.image} />;
}
